/**
 * ============================================================================
 * ExpenseManagerSheet — S19.8 Gestor Dinámico de Gastos
 * ============================================================================
 * Panel lateral (Sheet) con tabla redimensionable para auditar, editar y añadir
 * gastos al escenario de simulación pro-forma.
 * ============================================================================
 */

import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, RotateCcw, Sparkles } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Table, TableBody, TableHeader, TableRow, TableCell } from "@/components/ui/table";
import { ResizableTableHead } from "@/components/ResizableTableHead";
import type { EngineGastoInput } from "@/lib/landedCostEngine";
import type { SimulatedGasto, SimulatedGastoOverride } from "@/hooks/useProformaSimulations";
import { inferScope, type PresetScope } from "@/lib/expensePresets";

const SCOPE_LABEL: Record<PresetScope, string> = {
  origen: "Origen",
  internacional: "Intl",
  destino: "Destino",
};
const SCOPE_CLASS: Record<PresetScope, string> = {
  origen:
    "bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-950/40 dark:text-sky-300 dark:border-sky-800",
  internacional:
    "bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800",
  destino:
    "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800",
};

const MONEDAS_FALLBACK = ["COP", "USD", "EUR", "CNY", "MXN", "GBP", "JPY", "BRL"] as const;
const BASES = ["SIN_CLASIFICAR", "Peso", "Valor", "CBM", "Ninguna"] as const;
const BASE_LABELS: Record<string, string> = {
  SIN_CLASIFICAR: "— Selecciona —",
  Peso: "Peso",
  Valor: "Valor",
  CBM: "CBM",
  Ninguna: "Ninguna",
};

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  baseGastos: EngineGastoInput[];
  initialEditados: Record<string, SimulatedGastoOverride>;
  initialNuevos: SimulatedGasto[];
  trmMap?: Record<string, number>;
  /** TRM efectiva con overrides del Centro de Monedas aplicados. */
  effectiveTrmMap?: Record<string, number>;
  onApply: (params: {
    gastosEditados: Record<string, SimulatedGastoOverride>;
    gastosNuevos: SimulatedGasto[];
  }) => void;
  /** Elimina un gasto base del payload (V3). En V2, el panel lo inhabilita. */
  onRemoveBase?: (id: string) => void;
}

const fmtMoney = (n: number, moneda: string) => {
  const sym = moneda === "USD" ? "US$" : moneda === "EUR" ? "€" : "$";
  return `${sym}${n.toLocaleString("es-CO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const fmtCOP = (n: number) =>
  `$${n.toLocaleString("es-CO", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtTrm = (n: number) =>
  `$${n.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function newId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Celda con highlight si tiene override. */
const editedCellClass = (edited: boolean) =>
  edited ? "border-primary/50 bg-primary/5" : "";

interface RowProps {
  g: SimulatedGasto & { trm_pago?: number | null };
  trmMap?: Record<string, number>;
  effectiveTrmMap?: Record<string, number>;
  isBase: boolean;
  hasOverride: boolean;
  editedFields: Set<keyof SimulatedGasto>;
  onUpdate: <K extends keyof SimulatedGasto>(key: K, value: SimulatedGasto[K]) => void;
  onToggleActive?: (active: boolean) => void;
  onReset?: () => void;
  onRemove?: () => void;
  monedaOptions: string[];
}

function GastoRow({
  g,
  trmMap,
  effectiveTrmMap,
  isBase,
  hasOverride,
  editedFields,
  onUpdate,
  onToggleActive,
  onReset,
  onRemove,
  monedaOptions,
}: RowProps) {
  const moneda = (g.moneda ?? "COP").toUpperCase();
  const ivaLocal = Number(g.iva_local ?? 0);
  const monto = Number(g.monto ?? 0);
  const total = monto + ivaLocal;
  const trmEff = moneda === "COP" ? null : Number(effectiveTrmMap?.[moneda] ?? trmMap?.[moneda] ?? 0);
  const hasTrmOverride = moneda !== "COP" &&
    effectiveTrmMap?.[moneda] != null &&
    Number(effectiveTrmMap[moneda]) !== Number(trmMap?.[moneda] ?? 0);
  const baseIsPending = (g.base_prorrateo as string) === "SIN_CLASIFICAR";
  const inactivo = g.aplica_costeo_inventario === false;
  const scope = inferScope(g.concepto || "");

  return (
    <TableRow className={inactivo ? "opacity-50" : ""}>
      {/* Concepto */}
      <TableCell className={`p-1.5 align-middle ${editedCellClass(editedFields.has("concepto"))}`}>
        <Input
          value={g.concepto}
          onChange={(e) => onUpdate("concepto", e.target.value)}
          placeholder="Ej. Demora portuaria"
          className="h-8 text-[13px]"
        />
      </TableCell>

      {/* Scope */}
      <TableCell className="p-1.5 align-middle">
        <Badge
          variant="outline"
          className={`text-[10px] px-1.5 py-0 h-5 ${SCOPE_CLASS[scope]}`}
        >
          {SCOPE_LABEL[scope]}
        </Badge>
      </TableCell>

      {/* Base prorrateo */}
      <TableCell className={`p-1.5 align-middle ${editedCellClass(editedFields.has("base_prorrateo"))}`}>
        <Select
          value={g.base_prorrateo}
          onValueChange={(v) => onUpdate("base_prorrateo", v as SimulatedGasto["base_prorrateo"])}
        >
          <SelectTrigger
            className={`h-8 text-[13px] ${
              baseIsPending
                ? "border-amber-500 bg-amber-50 text-amber-900 ring-1 ring-amber-300 dark:bg-amber-950/30 dark:text-amber-200"
                : ""
            }`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(isBase ? BASES.filter((b) => b !== "SIN_CLASIFICAR") : BASES).map((b) => (
              <SelectItem key={b} value={b} className="text-[13px]">
                {BASE_LABELS[b] ?? b}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      {/* Moneda */}
      <TableCell className={`p-1.5 align-middle ${editedCellClass(editedFields.has("moneda"))}`}>
        <Select value={moneda} onValueChange={(v) => onUpdate("moneda", v)}>
          <SelectTrigger className="h-8 text-[13px] font-mono">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {monedaOptions.map((m) => (
              <SelectItem key={m} value={m} className="font-mono text-[13px]">
                {m}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>

      {/* TRM aplicada */}
      <TableCell className="p-1.5 align-middle">
        <div
          className={`h-8 rounded-md border bg-muted/30 px-2 py-1.5 text-right text-[13px] tabular-nums ${
            hasTrmOverride
              ? "font-semibold text-foreground bg-primary/10 border-primary/40"
              : "text-muted-foreground"
          }`}
        >
          {moneda === "COP" ? "—" : trmEff != null && trmEff > 0 ? fmtTrm(trmEff) : "—"}
        </div>
      </TableCell>

      {/* Monto */}
      <TableCell className={`p-1.5 align-middle ${editedCellClass(editedFields.has("monto"))}`}>
        <Input
          type="number"
          inputMode="decimal"
          step="0.01"
          value={g.monto || ""}
          onChange={(e) => onUpdate("monto", Number(e.target.value) || 0)}
          placeholder="0.00"
          className="h-8 text-right text-[13px] tabular-nums"
        />
      </TableCell>

      {/* IVA local */}
      <TableCell className={`p-1.5 align-middle ${editedCellClass(editedFields.has("iva_local"))}`}>
        <Input
          type="number"
          inputMode="decimal"
          step="0.01"
          value={ivaLocal || ""}
          onChange={(e) => onUpdate("iva_local", Number(e.target.value) || 0)}
          placeholder="0.00"
          className="h-8 text-right text-[13px] tabular-nums"
        />
      </TableCell>

      {/* TOTAL */}
      <TableCell className="p-1.5 align-middle">
        {(() => {
          const totalEnCop =
            moneda === "COP"
              ? total
              : trmEff != null && trmEff > 0
                ? total * trmEff
                : null;
          return (
            <div className="h-8 rounded-md border bg-muted/30 px-2 py-1.5 text-right text-[13px] font-medium tabular-nums whitespace-nowrap">
              {totalEnCop != null ? fmtCOP(totalEnCop) : "Sin TRM"}
            </div>
          );
        })()}
      </TableCell>

      {/* Acciones */}
      <TableCell className="p-1.5 align-middle">
        <ActionsCell
          isBase={isBase}
          inactivo={inactivo}
          hasOverride={hasOverride}
          concepto={g.concepto || "(sin concepto)"}
          onToggleActive={onToggleActive}
          onReset={onReset}
          onRemove={onRemove}
        />
      </TableCell>
    </TableRow>
  );
}

interface ActionsCellProps {
  isBase: boolean;
  inactivo: boolean;
  hasOverride: boolean;
  concepto: string;
  onToggleActive?: (active: boolean) => void;
  onReset?: () => void;
  onRemove?: () => void;
}

function ActionsCell({
  isBase,
  inactivo,
  hasOverride,
  concepto,
  onToggleActive,
  onReset,
  onRemove,
}: ActionsCellProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleConfirm = () => {
    onRemove?.();
    setConfirmOpen(false);
  };

  return (
    <div className="flex items-center justify-end gap-2">
      {onToggleActive && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Switch
                  checked={!inactivo}
                  onCheckedChange={(v) => onToggleActive(v)}
                  className="scale-75"
                />
              </span>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p className="text-xs">Incluir en costeo de inventario</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {isBase && hasOverride && onReset && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-muted-foreground hover:text-foreground"
          onClick={onReset}
          title="Restaurar valores originales"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground hover:text-destructive"
        onClick={() => setConfirmOpen(true)}
        title="Eliminar"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este gasto?</AlertDialogTitle>
            <AlertDialogDescription>
              {`El gasto "${concepto}" se eliminará del escenario.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function ExpenseManagerSheet({
  open,
  onOpenChange,
  baseGastos,
  initialEditados,
  initialNuevos,
  trmMap,
  effectiveTrmMap,
  onApply,
  onRemoveBase,
}: Props) {
  const [editados, setEditados] = useState<Record<string, SimulatedGastoOverride>>({});
  const [nuevos, setNuevos] = useState<SimulatedGasto[]>([]);

  useEffect(() => {
    if (open) {
      setEditados({ ...initialEditados });
      setNuevos([...initialNuevos]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const monedaOptions = useMemo(() => {
    const fromTrm = Object.keys(trmMap ?? {})
      .map((c) => c.toUpperCase())
      .filter((c) => c === "COP" || Number(trmMap?.[c] ?? 0) > 0);
    return Array.from(new Set<string>([...MONEDAS_FALLBACK, ...fromTrm])).sort();
  }, [trmMap]);

  const patchOverride = (id: string, patch: Partial<SimulatedGastoOverride>) => {
    setEditados((prev) => {
      const next = { ...prev };
      const merged = { ...(next[id] ?? {}), ...patch };
      const isEmpty =
        merged.monto === undefined &&
        merged.base_prorrateo === undefined &&
        merged.concepto === undefined &&
        merged.moneda === undefined &&
        merged.aplica_costeo_inventario === undefined &&
        (merged.iva_local === undefined || merged.iva_local === 0);
      if (isEmpty) delete next[id];
      else next[id] = merged;
      return next;
    });
  };

  const resetEditado = (id: string) => {
    setEditados((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const addNuevo = () => {
    setNuevos((prev) => [
      ...prev,
      {
        id: newId(),
        concepto: "",
        monto: 0,
        moneda: "COP",
        base_prorrateo: "SIN_CLASIFICAR" as SimulatedGasto["base_prorrateo"],
        aplica_costeo_inventario: true,
      },
    ]);
  };

  const updateNuevo = <K extends keyof SimulatedGasto>(
    id: string,
    key: K,
    value: SimulatedGasto[K],
  ) => {
    setNuevos((prev) => prev.map((g) => (g.id === id ? { ...g, [key]: value } : g)));
  };

  const removeNuevo = (id: string) => {
    setNuevos((prev) => prev.filter((g) => g.id !== id));
  };

  const dirtyCount = useMemo(
    () => Object.keys(editados).length + nuevos.length,
    [editados, nuevos],
  );

  const nuevosValidos = nuevos.filter(
    (g) => g.concepto.trim() && Number.isFinite(Number(g.monto ?? 0)),
  );
  const tieneSinClasificar = nuevos.some(
    (g) => (g.base_prorrateo as string) === "SIN_CLASIFICAR",
  );

  const handleApply = () => {
    onApply({
      gastosEditados: editados,
      gastosNuevos: nuevosValidos,
    });
    onOpenChange(false);
  };

  /** Update sobre un gasto base — resuelve si cada campo difiere del original. */
  const updateBase = <K extends keyof SimulatedGasto>(
    gasto: EngineGastoInput,
    key: K,
    value: SimulatedGasto[K],
  ) => {
    const patch: Partial<SimulatedGastoOverride> = {};
    if (key === "monto") {
      const original = Number(gasto.monto ?? 0);
      const parsed = Number(value ?? 0);
      patch.monto = Math.abs(parsed - original) < 0.005 ? undefined : parsed;
    } else if (key === "base_prorrateo") {
      patch.base_prorrateo =
        value === gasto.base_prorrateo ? undefined : (value as SimulatedGasto["base_prorrateo"]);
    } else if (key === "iva_local") {
      const parsed = Number(value ?? 0);
      patch.iva_local = !Number.isFinite(parsed) || parsed === 0 ? undefined : parsed;
    } else if (key === "concepto") {
      const original = gasto.concepto ?? "";
      const v = String(value ?? "");
      patch.concepto = v === original ? undefined : v;
    } else if (key === "moneda") {
      const original = (gasto.moneda ?? "COP").toString().toUpperCase();
      const v = String(value ?? "").toUpperCase();
      patch.moneda = v === original ? undefined : v;
    } else if (key === "aplica_costeo_inventario") {
      const original = gasto.aplica_costeo_inventario ?? true;
      const v = Boolean(value);
      patch.aplica_costeo_inventario = v === original ? undefined : v;
    }
    patchOverride(gasto.id, patch);
  };

  const editedFieldsForBase = (
    gasto: EngineGastoInput,
    override: SimulatedGastoOverride | undefined,
  ): Set<keyof SimulatedGasto> => {
    const s = new Set<keyof SimulatedGasto>();
    if (!override) return s;
    if (override.monto !== undefined) s.add("monto");
    if (override.base_prorrateo !== undefined) s.add("base_prorrateo");
    if (override.iva_local !== undefined && override.iva_local !== 0) s.add("iva_local");
    if (override.concepto !== undefined) s.add("concepto");
    if (override.moneda !== undefined) s.add("moneda");
    if (override.aplica_costeo_inventario !== undefined) s.add("aplica_costeo_inventario");
    return s;
  };

  const renderTable = (
    rows: React.ReactNode,
  ) => (
    <div className="rounded-md border overflow-x-auto">
      <Table style={{ tableLayout: "fixed", width: "100%" }} className="text-sm">
        <TableHeader>
          <TableRow>
            <ResizableTableHead initialWidth={200} minWidth={120}>
              Concepto
            </ResizableTableHead>
            <ResizableTableHead initialWidth={90} minWidth={70}>
              Scope
            </ResizableTableHead>
            <ResizableTableHead initialWidth={100} minWidth={80}>
              Base Prorrateo
            </ResizableTableHead>
            <ResizableTableHead initialWidth={70} minWidth={60}>
              Moneda
            </ResizableTableHead>
            <ResizableTableHead initialWidth={90} minWidth={70} className="text-right">
              TRM Aplicada
            </ResizableTableHead>
            <ResizableTableHead initialWidth={110} minWidth={80} className="text-right">
              Monto
            </ResizableTableHead>
            <ResizableTableHead initialWidth={90} minWidth={70} className="text-right">
              IVA Local
            </ResizableTableHead>
            <ResizableTableHead initialWidth={130} minWidth={100} className="text-right">
              TOTAL
            </ResizableTableHead>
            <ResizableTableHead initialWidth={90} minWidth={70} className="text-right">
              Acciones
            </ResizableTableHead>
          </TableRow>
        </TableHeader>
        <TableBody>{rows}</TableBody>
      </Table>
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[95vw] lg:max-w-[1200px] xl:max-w-[1400px] p-0 flex flex-col gap-0"
      >
        <SheetHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between gap-2">
            <div className="space-y-1">
              <SheetTitle className="flex items-center gap-2 text-base">
                <Sparkles className="h-4 w-4 text-primary" />
                Gestor de Gastos del Escenario
              </SheetTitle>
              <SheetDescription className="text-xs">
                Edita montos base, inhabilita gastos o inyecta imprevistos. Los cambios solo afectan a esta simulación.
                Arrastra los bordes de las columnas para ajustar el ancho.
              </SheetDescription>
            </div>
            {dirtyCount > 0 && (
              <Badge variant="outline" className="shrink-0 text-[10px]">
                {dirtyCount} cambio{dirtyCount === 1 ? "" : "s"}
              </Badge>
            )}
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="px-6 py-4 space-y-3">
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Gastos del escenario
                  </h3>
                  <span className="text-[11px] text-muted-foreground tabular-nums">
                    {baseGastos.length + nuevos.length} gasto
                    {baseGastos.length + nuevos.length === 1 ? "" : "s"}
                  </span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 gap-1.5"
                  onClick={addNuevo}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Añadir gasto
                </Button>
              </div>

              {baseGastos.length + nuevos.length === 0 ? (
                <div className="rounded-md border border-dashed py-8 text-center text-xs text-muted-foreground">
                  Sin gastos en el escenario. Pulsa "Añadir gasto" para agregar uno.
                </div>
              ) : (
                renderTable(
                  <>
                    {baseGastos.map((g) => {
                      const override = editados[g.id];
                      const moneda = (override?.moneda ?? g.moneda ?? "COP").toString().toUpperCase();
                      const cardGasto: SimulatedGasto & { trm_pago?: number | null; iva_local?: number } = {
                        id: g.id,
                        concepto: override?.concepto ?? g.concepto ?? "Gasto",
                        monto: Number(override?.monto ?? g.monto ?? 0),
                        moneda,
                        base_prorrateo: (override?.base_prorrateo ?? g.base_prorrateo ?? "Valor") as SimulatedGasto["base_prorrateo"],
                        aplica_costeo_inventario:
                          override?.aplica_costeo_inventario ?? g.aplica_costeo_inventario ?? true,
                        iva_local: Number(override?.iva_local ?? 0),
                        trm_pago: g.trm_pago != null ? Number(g.trm_pago) : null,
                      };
                      return (
                        <GastoRow
                          key={g.id}
                          g={cardGasto}
                          trmMap={trmMap}
                          effectiveTrmMap={effectiveTrmMap}
                          isBase
                          hasOverride={override !== undefined}
                          editedFields={editedFieldsForBase(g, override)}
                          onUpdate={(key, value) => updateBase(g, key, value)}
                          onToggleActive={(active) => updateBase(g, "aplica_costeo_inventario", active)}
                          onReset={() => resetEditado(g.id)}
                          onRemove={
                            onRemoveBase
                              ? () => onRemoveBase(g.id)
                              : () => updateBase(g, "aplica_costeo_inventario", false)
                          }
                          monedaOptions={monedaOptions}
                        />
                      );
                    })}
                    {nuevos.map((g) => (
                      <GastoRow
                        key={g.id}
                        g={g}
                        trmMap={trmMap}
                        effectiveTrmMap={effectiveTrmMap}
                        isBase={false}
                        hasOverride={false}
                        editedFields={new Set()}
                        onUpdate={(key, value) => updateNuevo(g.id, key, value)}
                        onToggleActive={(active) => updateNuevo(g.id, "aplica_costeo_inventario", active)}
                        onRemove={() => removeNuevo(g.id)}
                        monedaOptions={monedaOptions}
                      />
                    ))}
                  </>,
                )
              )}
            </section>
          </div>
        </div>

        <SheetFooter className="px-6 py-3 border-t bg-muted/20 flex-row sm:justify-between items-center gap-2">
          <div className="text-[11px] text-muted-foreground">
            {Object.keys(editados).length} editado{Object.keys(editados).length === 1 ? "" : "s"} ·{" "}
            {nuevosValidos.length} nuevo{nuevosValidos.length === 1 ? "" : "s"} válido
            {nuevosValidos.length === 1 ? "" : "s"}
            {tieneSinClasificar && (
              <span className="ml-2 text-amber-600 dark:text-amber-400">
                · Selecciona la base de prorrateo antes de guardar.
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleApply}
              disabled={tieneSinClasificar}
              className="gap-1.5"
              title={tieneSinClasificar ? "Selecciona la base de prorrateo antes de guardar." : undefined}
            >
              <Sparkles className="h-3.5 w-3.5" />
              Aplicar al escenario
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
